import { type Path } from './paths'
import {
  type LicensedEstablishment,
  type LicensedEstablishmentUserLicensedEstablishment,
} from 'src/types/api'
import * as _ from 'lodash'
import { includes } from 'lodash'

const allPermissions = [
  'flexPayPermission',
  'compliancePermission',
  'gamingReportsPermission',
  'playerPaybackPermission',
  'patronCheckInsPermission',
  'rewardsPermission',
  'promotionsPermission',
  'sweepstakesPermission',
  'enrollInSweepstakesPermission',
  'locationSweepstakesPermission',
  'playerPaybackReportsPermission',
  'locationsPermission',
  'leUsersPermission',
  'contactUsPermission',
  'liveAtLocationViewPermission',
  'patronsAccountsPermission',
  'reportsFinancialPermission',
  'reportsNonFinancialPermission',
  'licensedEstablishmentUserId',
  'licensedEstablishmentId',
  'badgeRewardsPermission',
  'badgeLocationsPermission',
] as const

type PermissionTuple = typeof allPermissions
type Permission = PermissionTuple[number]
type UserPermissionSet = Pick<
  LicensedEstablishmentUserLicensedEstablishment,
  Permission
>

type AlwaysVisiblePath = 'always'
type LoginRequiredPath = 'loginRequired'
type PermissionedPath = Permission | Permission[]

export const pathPermissions: Record<
  Path,
  AlwaysVisiblePath | LoginRequiredPath | PermissionedPath
> = {
  '/': 'always',
  '/CreateAccount': 'always',
  '/Home': 'loginRequired',
  '/Development/Home': 'loginRequired',
  '/Compliance': 'compliancePermission',
  '/ContactUs': 'contactUsPermission',
  '/LicensedEstablishments/:id': 'leUsersPermission',
  '/LicensedEstablishmentUsers': 'leUsersPermission',
  '/LicensedEstablishmentUsers/NewLEConnectUserPage': 'leUsersPermission',
  '/LicensedEstablishmentUsers/LEConnectUserDetailsPage/:id':
    'leUsersPermission',
  // '/LiveAtLocation': 'liveAtLocationViewPermission',
  // '/LiveAtLocation/PatronDetails/:id/LicensedEstablishment/:licensedEstablishmentId':
  // 'liveAtLocationViewPermission',
  // '/LiveAtLocation/PatronDetails/:id': 'liveAtLocationViewPermission',
  '/Locations': 'locationsPermission',
  '/PatronCheckIns': 'patronCheckInsPermission',
  '/PlayerPayback': 'playerPaybackPermission',
  '/Specials&Events': 'promotionsPermission',
  '/Specials&Events/:id': 'promotionsPermission',
  '/FlexPay': 'flexPayPermission',
  '/InfoCenter/Newsletters': 'loginRequired',
  '/InfoCenter/News': 'loginRequired',
  '/InfoCenter/Resources': 'loginRequired',
  '/GamingReports': 'gamingReportsPermission',
  '/GamingReports/GamingReportsPage': 'gamingReportsPermission',
  '/GamingReports/WeeklySnapshot': 'gamingReportsPermission',
  '/GamingReports/WeeklySnapshotLocationSelect': 'gamingReportsPermission',
  '/GamingReports/PerformanceTrends': 'gamingReportsPermission',
  '/GamingReports/PerformanceTrendsTablePage': 'gamingReportsPermission',
  '/GamingReports/CreditsWageredByMachine': 'gamingReportsPermission',
  '/GamingReports/CreditsWageredByHour': 'gamingReportsPermission',
  '/GamingReports/VgtSnapshot': 'gamingReportsPermission',
  '/GamingReports/VgtTrends': 'gamingReportsPermission',
  '/GamingReports/CreditsWageredHourlyTrends': 'gamingReportsPermission',
  '/GamingReports/TrailingNtiTrends': 'gamingReportsPermission',
  '/PlayerPaybackReports/PatronRewardRedemptions':
    'playerPaybackReportsPermission',
  '/PlayerPaybackReports/PatronList': 'playerPaybackReportsPermission',
  '/PlayerPaybackReports/PatronStats': 'playerPaybackReportsPermission',
  '/PlayerPaybackReports/PatronActivity': 'playerPaybackReportsPermission',
  '/Sweepstakes': 'sweepstakesPermission',
  '/Sweepstakes/:id/Overview': 'sweepstakesPermission',
  '/Sweepstakes/:id/Winners': 'sweepstakesPermission',
  '/Sweepstakes/:id/ParticipatingLocations': 'sweepstakesPermission',
  '/Sweepstakes/:id/EnrollLocations': 'sweepstakesPermission',
  '/Sweepstakes/:id/AdditionalEnrollmentDetails': 'sweepstakesPermission',
  '/Sweepstakes/:id/ReviewLocationsToEnroll': 'sweepstakesPermission',
  '/LocationSweepstakes': 'locationSweepstakesPermission',
  '/LocationSweepstakes/:id/Overview': 'locationSweepstakesPermission',
  '/LocationSweepstakes/:id/Winners': 'locationSweepstakesPermission',
  '/LocationSweepstakes/:id/ParticipatingLocations':
    'locationSweepstakesPermission',
  '/LocationSweepstakes/:id/Settings': 'locationSweepstakesPermission',
  '/Rewards': 'rewardsPermission',
  '/RewardsProgram/:id': 'rewardsPermission',
  // '/RewardsProgram/:id/Settings': 'rewardsPermission',
  '/RewardsProgram/:id/Redemption': 'rewardsPermission',
  '/RewardsProgram/:id/ActivityLog': 'rewardsPermission',
  '/RewardsProgram/:id/RewardsCatalog/:rewardCatalogId': 'rewardsPermission',
  '/LocationBadges': 'badgeLocationsPermission',
  '/LocationBadges/:id': 'badgeLocationsPermission',
}

const pathPermissionsAsRegExps: Array<
  [RegExp, AlwaysVisiblePath | LoginRequiredPath | PermissionedPath]
> = _.map(pathPermissions, (v, k) => {
  return [new RegExp('^' + k.replace(/:\w*id/gi, String.raw`\d+`) + '$'), v]
})

const findPathPermission = (path: string) => {
  const match = _.find(pathPermissionsAsRegExps, (x) => x[0].test(path))
  if (_.isNil(match)) {
    return null
  }
  return match[1]
}

// In practice, you can call this with an LicensedEstablishmentUserLicensedEstablishment because
// UserPermissionSet is a strict subset of LicensedEstablishmentUserLicensedEstablishment
export const isPathAvailable = (
  path: string,
  permissions?: UserPermissionSet[]
): boolean => {
  const pathPermission = findPathPermission(path)

  if (pathPermission === 'always') {
    return true
  }

  if (!permissions) {
    return false
  }

  if (pathPermission === 'loginRequired') {
    return true
  }

  const availablePermissions = userPermissions(permissions) // Only need to look at the first collection since the permissions will be set for all based on the getLEPermissionSets function
  const requiredPermissions = [pathPermission].flat()
  return _.intersection(availablePermissions, requiredPermissions).length > 0
}

// Given a UserPermissionSet, build an array of permissions
function userPermissions(userPermissionSet: UserPermissionSet[]) {
  if (userPermissionSet !== undefined) {
    let flexPayPermission = false
    let compliancePermission = false
    let gamingReportsPermission = false
    let playerPaybackPermission = false
    let patronCheckInsPermission = false
    let rewardsPermission = false
    let promotionsPermission = false
    let sweepstakesPermission = false
    let locationSweepstakesPermission = false
    let playerPaybackReportsPermission = false
    let locationsPermission = false
    let leUsersPermission = false
    let contactUsPermission = false
    let patronsAccountsPermission = false
    let badgeRewardsPermission = false
    let badgeLocationsPermission = false

    userPermissionSet.forEach((item) => {
      if (item?.flexPayPermission) {
        flexPayPermission = true
      }
      if (item?.compliancePermission) {
        compliancePermission = true
      }
      if (item?.gamingReportsPermission) {
        gamingReportsPermission = true
      }
      if (item?.playerPaybackPermission) {
        playerPaybackPermission = true
      }
      if (item?.patronCheckInsPermission) {
        patronCheckInsPermission = true
      }
      if (item?.rewardsPermission) {
        rewardsPermission = true
      }
      if (item?.promotionsPermission) {
        promotionsPermission = true
      }
      if (item?.sweepstakesPermission) {
        sweepstakesPermission = true
      }
      if (item?.locationSweepstakesPermission) {
        locationSweepstakesPermission = true
      }
      if (item?.playerPaybackReportsPermission) {
        playerPaybackReportsPermission = true
      }
      if (item?.leUsersPermission) {
        leUsersPermission = true
      }
      if (item?.locationsPermission) {
        locationsPermission = true
      }
      if (item?.contactUsPermission) {
        contactUsPermission = true
      }
      if (item?.patronsAccountsPermission) {
        patronsAccountsPermission = true
      }
      if (item?.badgeRewardsPermission) {
        badgeRewardsPermission = true
      }
      if (item?.badgeLocationsPermission) {
        badgeLocationsPermission = true
      }
    })

    let validPermissions = [...allPermissions]

    if (!flexPayPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/flexPayPermission/.exec(x)
      )
    }
    if (!compliancePermission) {
      validPermissions = validPermissions.filter(
        (x) => !/compliancePermission/.exec(x)
      )
    }
    if (!gamingReportsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/gamingReportsPermission/.exec(x)
      )
    }
    if (!playerPaybackPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/playerPaybackPermission/.exec(x)
      )
    }
    if (!patronCheckInsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/patronCheckInsPermission/.exec(x)
      )
    }
    if (!rewardsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/rewardsPermission/.exec(x)
      )
    }
    if (!promotionsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/promotionsPermission/.exec(x)
      )
    }
    if (!sweepstakesPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/sweepstakesPermission/.exec(x)
      )
    }
    if (!locationSweepstakesPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/locationSweepstakesPermission/.exec(x)
      )
    }
    if (!playerPaybackReportsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/playerPaybackReportsPermission/.exec(x)
      )
    }
    if (!leUsersPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/leUsersPermission/.exec(x)
      )
    }
    if (!locationsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/locationsPermission/.exec(x)
      )
    }
    if (!contactUsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/contactUsPermission/.exec(x)
      )
    }
    if (!patronsAccountsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/patronsAccountsPermission/.exec(x)
      )
    }
    if (!badgeRewardsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/badgeRewardsPermission/.exec(x)
      )
    }
    if (!badgeLocationsPermission) {
      validPermissions = validPermissions.filter(
        (x) => !/badgeLocationsPermission/.exec(x)
      )
    }

    return validPermissions
  }
}

// This method encapsulates the getLEsForCA, getLEObjectsByLEIds and getLEPermissionSets methods
export const getLEPermissions = (
  leQuery: LicensedEstablishment[] | null | undefined,
  leUserQuery:
    | LicensedEstablishmentUserLicensedEstablishment[]
    | null
    | undefined,
  caId: number | undefined
) => {
  // Get the LE Id's associated with the selected corporate account
  const leIds = getLEsForCA(leQuery, caId)

  // Get the LE objects that include the LE Id's
  const leDatas = getLEObjectsByLEIds(leUserQuery, leIds)

  // For each LE object, check the permission
  // If the permission is true, set all other object permissions to true
  const leDataObjects = getLEPermissionSets(leDatas)

  return leDataObjects
}

// Get the LE Id's associated with the selected corporate account
// This method used with getLEObjectsByLEIds and getLEPermissionSets
export const getLEsForCA = (
  leQuery: LicensedEstablishment[] | null | undefined,
  caId: number | undefined
) => {
  const leIds =
    leQuery?.filter((x) => x.corporateAccountId === caId).map((x) => x.id) ??
    undefined

  return leIds
}

// Get the LE objects that include LE Id's
// This method used with getLEsForCA and getLEPermissionSets
export const getLEObjectsByLEIds = (
  leUserQuery:
    | LicensedEstablishmentUserLicensedEstablishment[]
    | null
    | undefined,
  leIds: number[] | undefined
) => {
  const leDatas =
    leUserQuery?.filter((x) => includes(leIds, x.licensedEstablishmentId)) ??
    undefined

  return leDatas
}

// For each LE object, check the permission
// If the permission is true, set all other object permissions to true
// This method used with getLEsForCA and getLEObjectsByLEIds
export const getLEPermissionSets = (
  leDatas: LicensedEstablishmentUserLicensedEstablishment[] | null | undefined
) => {
  const leDataObjects = leDatas as unknown as UserPermissionSet[]
  return leDataObjects
}
